import { createColumnHelper, ColumnDef } from "@tanstack/react-table";
import TableHeader from "components/TableHeader";
import TableCell from "components/TableCell";
import { useNavigate } from "react-router-dom";
import Table from "components/Table";
import { formatAmount } from "utils/utilityFunctions";

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface AllEmployeesTablePropsType {
  tableData: EmployeeType[] | [];
  paginationData: PaginatedResponse<EmployeeType> | null;
  currentPage: number;
  pageSize: number;
  getEmployeesPage: (pageUrl: string, action: string) => Promise<void>;
  onPageSizeChange?: (newSize: number) => void;
}

const EmployeesListTable = (props: AllEmployeesTablePropsType) => {
  const { 
    tableData, 
    paginationData,
    currentPage, 
    pageSize,
    getEmployeesPage,
    onPageSizeChange
  } = props;
  
  const navigate = useNavigate();
  const columnHelper = createColumnHelper<EmployeeType>();

  const handleRowClick = (employee: EmployeeType) => {
    navigate(`/employee/${employee.id}`);
  };

  const handleNextPage = () => {
    if (paginationData?.next) {
      getEmployeesPage(paginationData.next, "next");
    }
  };

  const handlePreviousPage = () => {
    if (paginationData?.previous) {
      getEmployeesPage(paginationData.previous, "previous");
    }
  };

  const columns: ColumnDef<EmployeeType, any>[] = [
    columnHelper.accessor("id", {
      id: "id",
      header: () => <TableHeader>ID</TableHeader>,
      cell: (info) => (
        <TableCell>
          {info.getValue()}
        </TableCell>
      ),
    }),
    columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
      id: "name",
      header: () => <TableHeader>Name</TableHeader>,
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
    }),
    columnHelper.accessor("designation", {
      id: "designation",
      header: () => <TableHeader>Designation</TableHeader>,
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
    }),
    columnHelper.accessor("phone", {
      id: "phone",
      header: () => <TableHeader>Phone</TableHeader>,
      cell: (info) => <TableCell>{info.getValue()}</TableCell>,
    }),
    columnHelper.accessor("total_income", {
      id: "total_income",
      header: () => <TableHeader>Total Wage</TableHeader>,
      cell: (info) => <TableCell>{formatAmount(info.getValue())}</TableCell>,
    }),
    columnHelper.accessor("date_joined", {
      id: "date_joined",
      header: () => <TableHeader>Start Date</TableHeader>,
      cell: (info) => (
        <TableCell>
          {info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "N/A"}
        </TableCell>
      ),
    }),
    columnHelper.accessor("left_at", {
      id: "left_at",
      header: () => <TableHeader>End Date</TableHeader>,
      cell: (info) => (
        <TableCell>
          {info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "N/A"}
        </TableCell>
      ),
    }),
    columnHelper.accessor("is_active", {
      id: "status",
      header: () => <TableHeader>Status</TableHeader>,
      cell: (info) => (
        <TableCell>
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold
            ${info.getValue() ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
          >
            {info.getValue() ? "Active" : "Inactive"}
          </span>
        </TableCell>
      ),
    }),
  ];

  const totalCount = paginationData?.count || 0;

  return (
    <Table<EmployeeType>
      tableData={tableData}
      columns={columns}
      tableHeading="All Employees"
      onRowClick={handleRowClick}
      totalCount={totalCount}
      currentPage={currentPage}
      pageSize={pageSize}
      hasNextPage={!!paginationData?.next}
      hasPreviousPage={!!paginationData?.previous}
      onNextPage={handleNextPage}
      onPreviousPage={handlePreviousPage}
      onPageSizeChange={onPageSizeChange}
    />
  );
};

export default EmployeesListTable;